import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

/**
 * TanStack Query setup.
 *
 * The client is created in state rather than at module scope so that each
 * render tree — including the one the prerenderer builds per route — gets its
 * own cache instead of sharing one across requests.
 */
const createClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Booking availability goes stale quickly; everything else is fine for a
        // minute. Individual queries override this where it matters.
        staleTime: 60_000,
        retry: (failureCount, error) => {
          // A 4xx will not fix itself on retry.
          const status = (error as { status?: number }).status;
          if (status && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });

export const QueryProvider = ({ children }: { children: ReactNode }) => {
  const [client] = useState(createClient);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};
